import { apiFetch, readApiJson } from '@/lib/api-fetch'
import {
  OnecPieceApplyQueuedSchema,
  OnecPieceLatestDiffSchema,
  OnecSyncRequestCreatedSchema,
  OnecSyncSettingsSchema,
  OnecSyncerStatusSchema,
  type OnecPieceApplyQueued,
  type OnecPieceDiff,
  type OnecPieceDiffFilter,
  type OnecSyncRequestCreated,
  type OnecSyncSettings,
  type OnecSyncerStatus,
} from '@/lib/api/schemas/onec-sync.schema'

export async function getOnecSyncStatus(signal?: AbortSignal): Promise<OnecSyncerStatus> {
  return OnecSyncerStatusSchema.parse(await readApiJson<unknown>(await apiFetch('/api/onec-sync/status', { signal })))
}

export async function requestOnecSync(full = false): Promise<OnecSyncRequestCreated> {
  const response = await apiFetch('/api/onec-sync/requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kinds: full ? ['costs', 'pieces_full'] : ['costs'] }),
  })
  return OnecSyncRequestCreatedSchema.parse(await readApiJson<unknown>(response))
}

export async function getOnecSyncSettings(signal?: AbortSignal): Promise<OnecSyncSettings> {
  return OnecSyncSettingsSchema.parse(await readApiJson<unknown>(await apiFetch('/api/onec-sync/settings', { signal })))
}

export async function updateOnecSyncSettings(change: { paused?: boolean; autoApply?: boolean; autoApplySales?: boolean }): Promise<OnecSyncSettings> {
  const response = await apiFetch('/api/onec-sync/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(change),
  })
  return OnecSyncSettingsSchema.parse(await readApiJson<unknown>(response))
}

export interface OnecDiffPageQuery {
  filter?: OnecPieceDiffFilter
  offset?: number
  limit?: number
}

export async function getOnecSyncDiff(query: OnecDiffPageQuery = {}, signal?: AbortSignal): Promise<OnecPieceDiff | null> {
  const params = new URLSearchParams({
    filter: query.filter ?? 'all',
    offset: String(query.offset ?? 0),
    limit: String(query.limit ?? 100),
  })
  const response = await apiFetch(`/api/onec-sync/diff?${params.toString()}`, { signal })
  return OnecPieceLatestDiffSchema.parse(await readApiJson<unknown>(response)).diff
}

/** Without `uins` every unapplied line of the diff is applied; with them, exactly those lines. */
export async function applyOnecSyncDiff(operationId: string, uins?: readonly string[]): Promise<OnecPieceApplyQueued> {
  const path = `/api/onec-sync/diff/${encodeURIComponent(operationId)}/apply`
  const response = uins === undefined
    ? await apiFetch(path, { method: 'POST' })
    : await apiFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uins }) })
  return OnecPieceApplyQueuedSchema.parse(await readApiJson<unknown>(response))
}
